import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import If from '@components/if/if.tsx';
import ChartLoader from '@shared/components/charts/chart-loader/chart-loader.tsx';
import { LineChartProps } from '@shared/models/charts/line-chart.model.ts';

const LineChart = <T extends Record<string, unknown>>({
  data,
  loading = false,
  config
}: LineChartProps<T>) => {
  const { t } = useTranslation();
  const {
    xAxisKey,
    dataKey,
    height = 300,
    color = 'var(--teal)',
    showDots = true,
    legendLabel,
    yAxisFormatter,
    tooltipFormatter,
    tooltipValueLabel,
    xAxisFormatter,
    tooltipLabelFormatter,
    curveType = 'linear'
  } = config;

  return (
    <div
      className="bg-(--white) rounded-xl p-4 overflow-hidden relative"
      style={{
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
      }}
    >
      <If
        loading={loading}
        condition={!loading}
        loadingContent={<ChartLoader height={height} />}
      >
        {legendLabel && (
          <div className="flex items-center mb-4 ml-4 h-8.5">
            <div
              className="w-2.5 h-2.5 rounded-[50%] mr-2"
              style={{ backgroundColor: color }}
            />
            <span className="text-[14px] font-normal text-(--blue-4)">
              {legendLabel}
            </span>
          </div>
        )}

        <ResponsiveContainer width="100%" height={height}>
          <RechartsLineChart
            data={data}
            margin={{ top: 20, right: 10, left: 0, bottom: 20 }}
          >
            <XAxis
              dataKey={xAxisKey as string}
              axisLine={false}
              tickLine={false}
              padding={{ left: 10, right: 10 }}
              tick={{ fill: 'var(--blue-4)', fontSize: 12 }}
              tickMargin={20}
              tickFormatter={xAxisFormatter}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: 'var(--blue-4)', fontSize: 12 }}
              tickFormatter={yAxisFormatter}
            />
            <CartesianGrid
              stroke="var(--gray-7)"
              strokeDasharray="3 3"
              strokeWidth={1}
            />
            <Line
              type={curveType}
              dataKey={dataKey as string}
              stroke={color}
              strokeWidth={1.5}
              dot={
                showDots
                  ? {
                      stroke: color,
                      strokeWidth: 1.5,
                      fill: 'var(--white)',
                      r: 3
                    }
                  : false
              }
              activeDot={
                showDots
                  ? {
                      stroke: color,
                      strokeWidth: 1.5,
                      fill: 'var(--teal)',
                      r: 4
                    }
                  : undefined
              }
            />
            <Tooltip
              contentStyle={{
                borderRadius: 10,
                border: 'none',
                boxShadow: '0 0 10px var(--black-alpha-100)'
              }}
              labelStyle={{ color: 'var(--dark-2)' }}
              labelFormatter={
                tooltipLabelFormatter
                  ? (label) => tooltipLabelFormatter(label as string)
                  : undefined
              }
              formatter={
                tooltipValueLabel || tooltipFormatter
                  ? (value) => {
                      const formatted = tooltipFormatter
                        ? tooltipFormatter(value as number)
                        : value;
                      return tooltipValueLabel
                        ? [formatted, t(tooltipValueLabel)]
                        : formatted;
                    }
                  : undefined
              }
            />
          </RechartsLineChart>
        </ResponsiveContainer>
      </If>
    </div>
  );
};

export default LineChart;
